import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const Lightbox = ({ project, startIdx = 0, onClose }) => {
  const imgs = useMemo(
    () =>
      project.images && project.images.length > 0 ? project.images : [project.image],
    [project]
  );
  const [i, setI] = useState(startIdx);
  const hasMany = imgs.length > 1;

  const prev = useCallback(
    () => setI((v) => (v - 1 + imgs.length) % imgs.length),
    [imgs]
  );
  const next = useCallback(
    () => setI((v) => (v + 1) % imgs.length),
    [imgs]
  );

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft' && hasMany) prev();
      else if (e.key === 'ArrowRight' && hasMany) next();
    };
    document.addEventListener('keydown', handleKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, prev, next, hasMany]);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4 md:p-8 animate-[fadeIn_.3s_ease]"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <button
        onClick={onClose}
        aria-label="Close"
        className="absolute top-5 right-5 md:top-8 md:right-8 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors z-10"
      >
        <X size={20} />
      </button>

      {hasMany && (
        <>
          <button
            onClick={(e) => {
              e.stopPropagation();
              prev();
            }}
            aria-label="Previous"
            className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors z-10"
          >
            <ChevronLeft size={22} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              next();
            }}
            aria-label="Next"
            className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors z-10"
          >
            <ChevronRight size={22} />
          </button>
        </>
      )}

      <div
        className="relative max-w-[1200px] w-full max-h-[85vh] flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={imgs[i]}
          alt={project.title}
          className="max-w-full max-h-[75vh] object-contain rounded-sm shadow-2xl"
        />
        <div className="mt-5 text-center px-4">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#B8935A] mb-1">
            {project.category}
          </div>
          <div className="font-serif text-2xl md:text-3xl text-white">{project.title}</div>
          {hasMany && (
            <div className="mt-3 flex items-center justify-center gap-1.5">
              {imgs.map((src, j) => (
                <button
                  key={`lb-${src}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setI(j);
                  }}
                  aria-label={`Show image ${j + 1}`}
                  className={`h-1.5 rounded-full transition-all ${
                    j === i ? 'w-6 bg-white' : 'w-1.5 bg-white/50 hover:bg-white/80'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Lightbox;
